// DR-017 Business-plane personal MFA boundary.
//
// Wraps the ENTIRE authenticated Business application (BusinessDashboard). It renders inside the
// /business/* ProtectedRoute, so it runs AFTER Firebase session restoration and a direct URL to any
// /business/* route cannot bypass it — the boundary owns the whole subtree.
//
// Behaviour is authoritative-server-driven and fail-open on indeterminacy:
//   • ONE authoritative POST /api/v1/mfa/status per (uid, tenant) after session/tenant restoration,
//     via mfaApi (which uses the canonical backend API base — never the Web origin).
//   • Single-flight: overlapping effects / re-renders / route changes share the in-flight request and
//     never issue duplicates. A stale response from a previous (uid, tenant) is ignored.
//   • No policy / already assured (MFA_NOT_REQUIRED | MFA_ASSURANCE_VALID) → transparent passthrough,
//     ZERO behaviour change for every current tenant until a SuperAdmin activates policy.
//   • Enrolment/challenge required → BLOCK with the personal MfaGate until assurance is obtained.
//   • A transient/network error (or 405-class) is NOT a logout and NOT a brick: we pass through —
//     mutation-level assurance is still enforced server-side (step-up on billing/invite/role).
//
// PERSONAL MFA only. It NEVER renders tenant MFA policy governance (SuperAdmin-plane only).
import React, { useEffect, useRef, useState } from 'react';
import MfaGate from './MfaGate';
import { mfaApi, MFA_STATES } from '../../../lib/mfaClient';
import { getFreshIdToken, auth } from '../../../lib/firebase';
import { useBusiness } from '../../../contexts/BusinessContext';

const getToken = () => getFreshIdToken();

// Module-level single-flight cache: composite key (uid|tenant) -> Promise. Prevents duplicate
// status requests across overlapping mounts/effects and React re-renders during auth restoration.
const _inFlight = new Map();
function statusOnce(key) {
  if (_inFlight.has(key)) return _inFlight.get(key);
  const p = mfaApi.status(getToken).finally(() => { _inFlight.delete(key); });
  _inFlight.set(key, p);
  return p;
}

export default function BusinessMfaBoundary({ children }) {
  const { currentBusiness } = useBusiness();
  const tenant = currentBusiness?.brandId || '';
  const [phase, setPhase] = useState('checking'); // checking | assured | gate
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    // MFA policy is TENANT-governed, so probing before the active tenant is known is not merely
    // early - it is unanswerable. The request went out with no X-Servisfy-Tenant, and a person
    // holding several memberships was correctly failed closed (409 AMBIGUOUS_TENANT_CONTEXT).
    // Proven in staging: every 409 was immediately followed by a 200 once the tenant landed, i.e.
    // one wasted, guaranteed-failing call on every mount and every tenant switch.
    // Stay in 'checking' until there is a tenant to ask about; the effect re-runs when it arrives.
    if (!tenant) return undefined;
    const uid = auth.currentUser?.uid || '';
    const key = `${uid}|${tenant}`;
    let ignore = false; // ignore a response if this effect was superseded (uid/tenant changed)
    (async () => {
      try {
        const r = await statusOnce(key);
        if (ignore || !mounted.current) return;      // stale identity/tenant — do not apply
        const st = r?.data?.state;
        if (!r?.ok || st === MFA_STATES.NOT_REQUIRED || st === MFA_STATES.ASSURANCE_VALID) {
          setPhase('assured');
          return;
        }
        setPhase('gate');
      } catch {
        if (!ignore && mounted.current) setPhase('assured'); // never lock the plane on a transient error
      }
    })();
    return () => { ignore = true; };
  }, [tenant]); // re-evaluate only when the active tenant changes

  useEffect(() => () => { mounted.current = false; }, []);

  if (phase === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" aria-label="Loading" />
      </div>
    );
  }

  if (phase === 'gate') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <MfaGate onAssured={() => mounted.current && setPhase('assured')} />
      </div>
    );
  }

  return children;
}
